import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shell/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAppState } from "@/state/AppStateProvider";
import { brandApi, brandsApi, mediaUrl } from "@/lib/apiClient";
import { Plus, Check, Trash2, Star, Mic, Building2, Sparkles, Save, Pencil, Eye, Globe, Image as ImageIcon, X as XIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useDismissed } from "@/lib/useDismissed";

export default function Brands() {
  const [explainerDismissed, dismissExplainer] = useDismissed("sce_brands_explainer_v1");
  const { state, createBrand, updateBrandMeta, deleteBrand, setActiveBrand, applyIndustryPack, createVoice, updateVoice, deleteVoice } = useAppState();
  const brands = useMemo(() => state.brands || [], [state.brands]);
  const activeId = state.activeBrandId;
  const [selectedId, setSelectedId] = useState(activeId);
  const selected = useMemo(() => brands.find((b) => b.id === (selectedId || activeId)) || brands[0] || null, [brands, selectedId, activeId]);

  const [newBrandName, setNewBrandName] = useState("");
  const [packs, setPacks] = useState([]);
  useEffect(() => { brandApi.packs().then(setPacks).catch(() => setPacks([])); }, []);
  useEffect(() => { if (!selectedId && activeId) setSelectedId(activeId); }, [activeId, selectedId]);

  // What the selected brand's industry actually does: the pack (label + tagline)
  // and the real domain-knowledge bullets injected (the sk_industry_<id> skill).
  const currentPack = useMemo(() => packs.find((p) => p.id === selected?.industry) || null, [packs, selected?.industry]);
  const industryKnowledge = useMemo(() => {
    const skill = (state.skills || []).find((s) => s.id === `sk_industry_${selected?.industry}`);
    const body = (skill?.markdown || "").replace(/^\s*---[\s\S]*?---\s*/, "").trim();
    return body.split("\n").map((l) => l.replace(/^[-*]\s*/, "").trim()).filter(Boolean);
  }, [state.skills, selected?.industry]);

  const onCreate = async () => {
    const name = newBrandName.trim();
    if (!name) return toast.error("Name your brand");
    const res = await createBrand({ name });
    if (res.ok) { setNewBrandName(""); setSelectedId(res.brand.id); toast.success(`Created "${name}"`); }
    else toast.error(res.error || "Couldn't create brand");
  };

  return (
    <div className="space-y-6" data-testid="brands-page">
      <PageHeader title="Brands" description="Each brand has its own voice(s), industry, and connected accounts. Pick one when you create content." />

      {/* How it works — dismissible first-run explainer */}
      {!explainerDismissed && (
      <div className="relative rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
        <button type="button" onClick={dismissExplainer} aria-label="Dismiss explainer" className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-white/[0.06]" data-testid="brands-explainer-dismiss">
          <XIcon className="h-3.5 w-3.5" />
        </button>
        <div className="mb-1.5 flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-[color:var(--accent-cyan)]" />
          <h2 className="text-[13.5px] font-semibold text-1">How a brand shapes every post</h2>
        </div>
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          When you create a post or blog, you pick a brand. The agent then writes using that brand's
          <span className="text-1 font-medium"> recipe</span>, in this order:
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[
            { icon: Mic, label: "Voice", body: "The exact tone, phrasing, and personality you write into a voice are injected verbatim — it controls HOW it sounds, never the topic. A brand can have several (e.g. Founder vs Casual)." },
            { icon: Building2, label: "Industry", body: "Adds domain knowledge for the brand's industry (what buyers care about, the language, what to avoid) and fills sensible profile defaults — applied automatically." },
            { icon: Pencil, label: "Writing style", body: "One content template per post (story, FAQ, case study, explainer…), picked at create time. Exactly one, so styles never conflict." },
            { icon: Eye, label: "Polish", body: "A critique pass runs automatically: tightens the draft, removes hedging, keeps one clear ask. You don't manage it." },
          ].map((x) => (
            <div key={x.label} className="flex gap-2.5 rounded-lg border p-2.5" style={{ borderColor: "var(--border-subtle)" }}>
              <x.icon className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--accent-cyan)]" />
              <div>
                <p className="text-[12.5px] font-semibold text-1">{x.label}</p>
                <p className="text-[11.5px] leading-relaxed text-muted-foreground">{x.body}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11.5px] text-muted-foreground">
          Brands are fully separate — editing one (or applying an industry pack) never touches another. Everything for a
          brand — its profile, voices, industry, and Instagram card — lives right here.
        </p>
      </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        {/* Brand list */}
        <aside className="space-y-2">
          {brands.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setSelectedId(b.id)}
              data-testid={`brand-row-${b.id}`}
              className={cn("flex w-full items-center justify-between gap-2 rounded-xl border p-3 text-left transition-colors",
                selected?.id === b.id ? "border-[color:var(--accent-blue)] bg-[color:var(--surface-2)]" : "hover:bg-[color:var(--surface-1)]")}
              style={{ borderColor: selected?.id === b.id ? undefined : "var(--border-subtle)" }}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate text-[13.5px] font-semibold text-1">{b.name || "Untitled brand"}</span>
                </div>
                <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                  {b.industry || "no industry"} · {(b.voices || []).length} voice{(b.voices || []).length === 1 ? "" : "s"}
                </p>
              </div>
              {b.id === activeId && <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-500">ACTIVE</span>}
            </button>
          ))}
          <div className="flex gap-2 pt-1">
            <Input value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="New brand name" data-testid="brand-new-name"
              onKeyDown={(e) => e.key === "Enter" && onCreate()} />
            <Button size="sm" onClick={onCreate} className="gap-1.5 shrink-0" data-testid="brand-create"><Plus className="h-3.5 w-3.5" /> Add</Button>
          </div>
        </aside>

        {/* Selected brand detail */}
        {selected && (
          <section className="space-y-5">
            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <BrandNameEditor
                  key={selected.id}
                  brand={selected}
                  onSave={async (name) => { const r = await updateBrandMeta(selected.id, { name }); r.ok ? toast.success("Brand name saved") : toast.error(r.error || "Save failed"); }}
                />
                <div className="flex items-center gap-2">
                  {selected.id !== activeId && (
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setActiveBrand(selected.id); toast.success(`Now working in ${selected.name}`); }} data-testid="brand-activate">
                      <Check className="h-3.5 w-3.5" /> Set active
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground hover:text-[color:var(--accent-red)]"
                    onClick={async () => { if (window.confirm(`Delete "${selected.name}"? Its content stays but loses this brand.`)) { const r = await deleteBrand(selected.id); r.ok ? toast.message("Brand deleted") : toast.error(r.error); } }}
                    data-testid="brand-delete"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                </div>
              </div>
              <p className="mt-3 rounded-lg border px-3 py-2 text-[12px] leading-relaxed text-foreground/85" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }} data-testid="brand-recipe-preview">
                Posts for <span className="font-medium text-1">{selected.name}</span> will sound like the{" "}
                <span className="font-medium text-1">&ldquo;{(selected.voices || []).find((v) => v.is_primary)?.name || (selected.voices || [])[0]?.name || "—"}&rdquo;</span> voice
                {selected.industry ? <>, draw on <span className="font-medium text-1">{selected.industry}</span> industry knowledge</> : <> (no industry set yet)</>}
                , take whichever writing style you pick at create time, and get an automatic polish pass.
              </p>
            </div>

            {/* Industry */}
            <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
              <div className="mb-2 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
                <h3 className="text-[13px] font-semibold text-1">Industry</h3>
              </div>
              <p className="mb-3 text-[12px] leading-relaxed text-muted-foreground">
                Choosing an industry does two things: it <span className="text-1">fills this brand's profile with proven defaults</span> (pillars,
                phrases to avoid — all editable below), and it makes the agent <span className="text-1">inject that field's domain
                knowledge</span> into every post for this brand.
              </p>
              <Select
                value={selected.industry || ""}
                onValueChange={async (v) => {
                  if (v === "custom") { const r = await updateBrandMeta(selected.id, { industry: "custom" }); r.ok ? toast.success("Custom industry — describe it below") : toast.error(r.error); return; }
                  const r = await applyIndustryPack(v, selected.id); r.ok ? toast.success(`Applied the ${v} industry to ${selected.name}`) : toast.error(r.error);
                }}
              >
                <SelectTrigger className="max-w-sm" data-testid="brand-industry"><SelectValue placeholder="Pick an industry…" /></SelectTrigger>
                <SelectContent>
                  {packs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-medium">{p.label || p.id}</span>
                      {p.tagline ? <span className="ml-2 text-muted-foreground">— {p.tagline}</span> : null}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">
                    <span className="font-medium">Custom…</span>
                    <span className="ml-2 text-muted-foreground">— write your own (AI agency, creator, media…)</span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Custom industry: the user writes their own field + what the agent should know. */}
              {selected.industry === "custom" ? (
                <CustomIndustryEditor
                  key={`ind-${selected.id}`}
                  brand={selected}
                  onSave={async (profile) => { const r = await updateBrandMeta(selected.id, { profile }); r.ok ? toast.success("Custom industry saved") : toast.error(r.error || "Save failed"); }}
                />
              ) : selected.industry ? (
                <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }} data-testid="brand-industry-explainer">
                  <p className="text-[12px] font-semibold text-1">{currentPack?.label || selected.industry}</p>
                  {currentPack?.tagline && <p className="text-[11.5px] text-muted-foreground">{currentPack.tagline}</p>}
                  {industryKnowledge.length > 0 && (
                    <>
                      <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Knowledge injected into every post</p>
                      <ul className="mt-1 space-y-1">
                        {industryKnowledge.map((line, i) => (
                          <li key={i} className="flex gap-1.5 text-[11.5px] leading-relaxed text-foreground/85">
                            <Check className="mt-0.5 h-3 w-3 shrink-0 text-[color:var(--accent-cyan)]" /> {line}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-[11.5px] text-muted-foreground">No industry set — posts will use the brand voice + your style pick, without extra domain framing.</p>
              )}
            </div>

            {/* Voices */}
            <VoicesEditor
              brand={selected}
              onCreate={(body) => createVoice(selected.id, body)}
              onUpdate={(vid, body) => updateVoice(selected.id, vid, body)}
              onDelete={(vid) => deleteVoice(selected.id, vid)}
            />

            {/* Profile */}
            <ProfileEditor
              key={`profile-${selected.id}`}
              brand={selected}
              pillarSuggestions={(currentPack?.pillars?.length && currentPack.pillars) || (selected.profile?.suggested_pillars?.length && selected.profile.suggested_pillars) || PILLAR_SUGGESTIONS}
              avoidSuggestions={(currentPack?.avoid?.length && currentPack.avoid) || (selected.profile?.suggested_avoid?.length && selected.profile.suggested_avoid) || AVOID_SUGGESTIONS}
              onSave={async (profile) => { const r = await updateBrandMeta(selected.id, { profile }); r.ok ? toast.success("Profile saved") : toast.error(r.error || "Save failed"); }}
            />

            {/* Instagram card */}
            <CardEditor
              key={`card-${selected.id}`}
              brand={selected}
              onSave={async (profile) => { const r = await updateBrandMeta(selected.id, { profile }); r.ok ? toast.success("Instagram card saved") : toast.error(r.error || "Save failed"); }}
            />
          </section>
        )}
      </div>
    </div>
  );
}

function BrandNameEditor({ brand, onSave }) {
  const [name, setName] = useState(brand.name || "");
  const dirty = name.trim() && name.trim() !== (brand.name || "");
  return (
    <div className="flex items-center gap-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs text-[15px] font-semibold" data-testid="brand-name-edit" />
      <Button size="sm" variant="outline" className="gap-1.5" disabled={!dirty} onClick={() => onSave(name.trim())} data-testid="brand-name-save">
        <Save className="h-3.5 w-3.5" /> Save
      </Button>
    </div>
  );
}

function VoiceRow({ voice, onUpdate, onDelete }) {
  const [name, setName] = useState(voice.name || "");
  const [instructions, setInstructions] = useState(voice.instructions || "");
  const dirty = name.trim() !== (voice.name || "") || instructions !== (voice.instructions || "");

  const save = async () => {
    if (!name.trim()) return toast.error("Voice needs a name");
    const r = await onUpdate(voice.id, { name: name.trim(), instructions });
    r.ok ? toast.success("Voice saved") : toast.error(r.error || "Save failed");
  };

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)" }} data-testid={`voice-${voice.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-[220px] text-[13px] font-medium" />
        <div className="flex items-center gap-1.5">
          {voice.is_primary
            ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-500"><Star className="h-3 w-3 fill-amber-500" /> Primary</span>
            : <Button size="sm" variant="ghost" className="h-7 gap-1 text-[11px] text-muted-foreground" onClick={async () => { const r = await onUpdate(voice.id, { is_primary: true }); r.ok && toast.success("Primary voice set"); }} data-testid={`voice-primary-${voice.id}`}><Star className="h-3 w-3" /> Make primary</Button>}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-[color:var(--accent-red)]" onClick={async () => { if (window.confirm(`Delete the "${voice.name}" voice?`)) { const r = await onDelete(voice.id); r.ok && toast.message("Voice deleted"); } }} data-testid={`voice-delete-${voice.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} placeholder="How this voice sounds — tone, phrasing, words to avoid…"
        className="mt-2 resize-none text-[12.5px]" style={{ backgroundColor: "var(--surface-2)" }} data-testid={`voice-instructions-${voice.id}`} />
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="outline" className="gap-1.5" disabled={!dirty} onClick={save} data-testid={`voice-save-${voice.id}`}>
          <Save className="h-3.5 w-3.5" /> Save voice
        </Button>
      </div>
    </div>
  );
}

// One-tap defaults users can pick from (they can still type their own).
const PILLAR_SUGGESTIONS = [
  "Behind the scenes", "Customer wins", "Tips & how-tos", "Common questions (FAQ)",
  "Seasonal & timely", "Team & culture", "Offers & promotions", "Local community",
  "Myth-busting", "Before & after",
];
const AVOID_SUGGESTIONS = [
  "game-changer", "revolutionary", "synergy", "world-class", "best-in-class",
  "cutting-edge", "seamless", "leverage", "unlock", "elevate", "supercharge", "next-level",
];

const PROFILE_PLATFORMS = [
  { id: "linkedin", label: "LinkedIn" }, { id: "x", label: "X" }, { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" }, { id: "threads", label: "Threads" },
  { id: "youtube_shorts", label: "Shorts" }, { id: "bluesky", label: "Bluesky" },
];

// Curated default CTA styles. The phrase is what gets injected; "custom" lets
// the user write their own. "none" means no standing CTA preference.
const CTA_STYLES = [
  { id: "soft", label: "Soft invite — low pressure", phrase: "a warm, low-pressure invitation to reply, DM, or visit when they're ready" },
  { id: "book", label: "Book a call / demo", phrase: "a clear invitation to book a quick call or demo" },
  { id: "dm", label: "DM a keyword", phrase: "ask them to comment or DM a specific keyword to get the details" },
  { id: "visit", label: "Visit the link", phrase: "send them to the website or landing-page link" },
  { id: "save", label: "Save / share the post", phrase: "ask them to save the post or share it with someone who needs it" },
  { id: "call_now", label: "Call now — act today", phrase: "a direct, act-now nudge to call or book today" },
  { id: "none", label: "No standing preference", phrase: "" },
];

// Starter voices — well-formed examples the user can drop in and then tweak.
const VOICE_PRESETS = [
  { name: "Professional", instructions: "Clear, credible, concise. Plain English — no jargon or hype. Confident but never salesy. Short sentences; lead with the point." },
  { name: "Friendly", instructions: "Warm and conversational, like a helpful neighbor. Use contractions and simple words, with a little personality. Encouraging, never condescending." },
  { name: "Bold", instructions: "Punchy and direct with a strong point of view. Short, declarative lines. Open with a hook. Confident takes backed by specifics. No hedging." },
  { name: "Expert", instructions: "Authoritative and educational. Explain the 'why' simply, share the insider detail most people miss, end with one practical takeaway. Calm and precise." },
  { name: "Down-to-earth", instructions: "Plain-spoken and blue-collar. Short, honest sentences, real-world words, zero corporate-speak. Talk like a trusted local who shows up and does the work." },
  { name: "Witty", instructions: "Light, clever, and human. A dry sense of humor and the occasional well-placed joke, but the point always lands. Never try-hard or cheesy." },
  { name: "Storyteller", instructions: "Lead with a small, specific moment or scene, then make the point. Concrete details over abstractions. Let the story carry the message; one gentle takeaway." },
  { name: "Inspirational", instructions: "Encouraging and motivating without being preachy. Focus on possibility and momentum. Warm, uplifting, and concrete — earn the optimism, don't just assert it." },
  { name: "Educational", instructions: "Helpful and teacherly. Break one idea down step by step, define terms in plain words, and leave the reader able to do something. Calm, generous, no hype." },
  { name: "Premium", instructions: "Polished, confident, and understated. Refined word choice, calm authority, lots of white space in the phrasing. Quality over loudness; never flashy or salesy." },
];

function CustomIndustryEditor({ brand, onSave }) {
  const p = brand.profile || {};
  const [label, setLabel] = useState(p.industry_label || "");
  const [knowledge, setKnowledge] = useState(p.industry_knowledge || "");
  const [suggested, setSuggested] = useState({ pillars: p.suggested_pillars || [], avoid: p.suggested_avoid || [] });
  const [generating, setGenerating] = useState(false);

  const onGenerate = async () => {
    if (!label.trim()) return toast.error("Type an industry name first");
    setGenerating(true);
    try {
      const r = await brandsApi.generateIndustry({ name: label.trim() });
      setKnowledge(r.knowledge || "");
      setSuggested({ pillars: r.pillars || [], avoid: r.avoid || [] });
      toast.success("Drafted knowledge + pillar/avoid suggestions — Save, then tap them in Profile");
    } catch (e) {
      toast.error(`Couldn't generate: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }} data-testid="brand-custom-industry">
      <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Industry name</Label>
      <div className="mt-1.5 flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. AI automation agency · YouTube creator · marketing studio" className="text-[12.5px]" data-testid="custom-industry-label" />
        <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={onGenerate} disabled={generating} data-testid="custom-industry-generate">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate details
        </Button>
      </div>
      <Label className="mt-3 block text-[11px] uppercase tracking-[0.14em] text-muted-foreground">What the agent should know about this field</Label>
      <p className="mb-1.5 text-[11px] text-muted-foreground">Who your buyers are, what they care about, the language they use, what to avoid. Type your own, or hit <span className="text-1">Generate details</span> to draft it with your model — then edit.</p>
      <Textarea value={knowledge} onChange={(e) => setKnowledge(e.target.value)} rows={7} placeholder={"- Buyers are time-strapped agency owners who want done-for-you systems\n- They care about ROI and saving hours, not the tech jargon\n- Speak plainly; avoid buzzwords like 'synergy' or 'revolutionary'"} className="resize-none text-[12.5px]" style={{ backgroundColor: "var(--surface-1)" }} data-testid="custom-industry-knowledge" />
      {(suggested.pillars.length > 0 || suggested.avoid.length > 0) && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Also drafted{suggested.pillars.length ? ` ${suggested.pillars.length} pillar` : ""}{suggested.pillars.length && suggested.avoid.length ? " +" : ""}{suggested.avoid.length ? ` ${suggested.avoid.length} avoid` : ""} suggestion(s) — Save, then add them from the Profile section below.
        </p>
      )}
      <div className="mt-2 flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => onSave({ ...p, industry_label: label.trim(), industry_knowledge: knowledge.trim(), suggested_pillars: suggested.pillars, suggested_avoid: suggested.avoid })} data-testid="custom-industry-save"><Save className="h-3.5 w-3.5" /> Save custom industry</Button>
      </div>
    </div>
  );
}

// Small add/remove chip list for pillars + phrases-to-avoid, with optional
// one-tap suggestions (only those not already added are shown).
function ChipList({ items, onChange, placeholder, testId, accent = "var(--accent-cyan)", suggestions = [] }) {
  const [draft, setDraft] = useState("");
  const list = items || [];
  const add = (v) => { const x = (v ?? draft).trim(); if (x && !list.includes(x)) { onChange([...list, x]); if (v == null) setDraft(""); } };
  const remaining = suggestions.filter((s) => !list.includes(s));
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {list.map((x) => (
          <span key={x} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px]" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
            <span style={{ color: accent }}>{x}</span>
            <button type="button" onClick={() => onChange(list.filter((y) => y !== x))} className="text-muted-foreground hover:text-[color:var(--accent-red)]"><XIcon className="h-3 w-3" /></button>
          </span>
        ))}
        {list.length === 0 && <span className="text-[11.5px] text-muted-foreground">None yet.</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className="h-8 text-[12.5px]" />
        <Button type="button" size="sm" variant="outline" className="h-8 shrink-0" onClick={() => add()}><Plus className="h-3.5 w-3.5" /></Button>
      </div>
      {remaining.length > 0 && (
        <div className="mt-2">
          <p className="mb-1 text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground">Suggestions — tap to add</p>
          <div className="flex flex-wrap gap-1.5">
            {remaining.map((s) => (
              <button key={s} type="button" onClick={() => add(s)} className="rounded-full border border-dashed px-2 py-0.5 text-[11.5px] text-muted-foreground transition-colors hover:text-1" style={{ borderColor: "var(--border-subtle)" }}>
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CtaStylePicker({ value, onChange }) {
  // Map the stored phrase back to a preset; unknown non-empty text => custom.
  const preset = CTA_STYLES.find((c) => c.phrase === (value || ""));
  const initialId = preset ? preset.id : ((value || "").trim() ? "custom" : "soft");
  const [mode, setMode] = useState(initialId);
  return (
    <>
      <Select
        value={mode}
        onValueChange={(id) => {
          setMode(id);
          if (id === "custom") { if (CTA_STYLES.some((c) => c.phrase === value)) onChange(""); return; }
          onChange(CTA_STYLES.find((c) => c.id === id)?.phrase ?? "");
        }}
      >
        <SelectTrigger className="max-w-sm" data-testid="brand-profile-cta"><SelectValue /></SelectTrigger>
        <SelectContent>
          {CTA_STYLES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          <SelectItem value="custom">Custom…</SelectItem>
        </SelectContent>
      </Select>
      {mode === "custom" && (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Describe your preferred CTA style…" className="mt-2 text-[12.5px]" data-testid="brand-profile-cta-custom" />
      )}
      {mode !== "custom" && CTA_STYLES.find((c) => c.id === mode)?.phrase
        ? <p className="mt-1 text-[11px] text-muted-foreground">Injected as: “{CTA_STYLES.find((c) => c.id === mode).phrase}”</p>
        : null}
    </>
  );
}

function ProfileEditor({ brand, onSave, pillarSuggestions = PILLAR_SUGGESTIONS, avoidSuggestions = AVOID_SUGGESTIONS }) {
  const p = brand.profile || {};
  const [draft, setDraft] = useState({
    business_description: p.business_description || p.what_you_do || "",
    primary_audience: p.primary_audience || "",
    website: p.website || "",
    preferred_cta_style: p.preferred_cta_style || "",
    content_pillars: p.content_pillars || [],
    phrases_to_avoid: p.phrases_to_avoid || [],
    default_platforms: p.default_platforms || [],
  });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const togglePlatform = (id) => set("default_platforms", draft.default_platforms.includes(id) ? draft.default_platforms.filter((x) => x !== id) : [...draft.default_platforms, id]);

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="brand-profile">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Pencil className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
        <h3 className="text-[13px] font-semibold text-1">Profile</h3>
        <span className="text-[11.5px] text-muted-foreground">— real background injected into every post (not the topic).</span>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">What this business does</Label>
          <Textarea rows={2} value={draft.business_description} onChange={(e) => set("business_description", e.target.value)} placeholder="e.g. AI voice agents that book jobs for local home-service businesses." className="mt-1.5 resize-none text-[12.5px]" style={{ backgroundColor: "var(--surface-2)" }} data-testid="brand-profile-desc" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Primary audience</Label>
            <Input value={draft.primary_audience} onChange={(e) => set("primary_audience", e.target.value)} placeholder="e.g. local plumbers & HVAC owners" className="mt-1.5 text-[12.5px]" data-testid="brand-profile-audience" />
          </div>
          <div>
            <Label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground"><Globe className="h-3 w-3" /> Website (default link)</Label>
            <Input value={draft.website} onChange={(e) => set("website", e.target.value)} placeholder="https://adamdoesai.com" className="mt-1.5 text-[12.5px]" data-testid="brand-profile-website" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Preferred CTA style</Label>
          <p className="mb-1.5 text-[11px] text-muted-foreground">The default flavor of ask, when a post has a CTA. Pick one or write your own.</p>
          <CtaStylePicker value={draft.preferred_cta_style} onChange={(v) => set("preferred_cta_style", v)} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Content pillars</Label>
          <p className="mb-1.5 text-[11px] text-muted-foreground">Recurring themes the agent leans on.</p>
          <ChipList items={draft.content_pillars} onChange={(v) => set("content_pillars", v)} placeholder="Add a pillar…" testId="brand-profile-pillars" suggestions={pillarSuggestions} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Phrases to avoid</Label>
          <p className="mb-1.5 text-[11px] text-muted-foreground">Hard "never say" rules.</p>
          <ChipList items={draft.phrases_to_avoid} onChange={(v) => set("phrases_to_avoid", v)} placeholder="Add a phrase to avoid…" testId="brand-profile-avoid" accent="var(--accent-red)" suggestions={avoidSuggestions} />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Default platforms</Label>
          <p className="mb-1.5 text-[11px] text-muted-foreground">Pre-selected on the Create screen for this brand.</p>
          <div className="flex flex-wrap gap-1.5">
            {PROFILE_PLATFORMS.map((pl) => {
              const on = draft.default_platforms.includes(pl.id);
              return (
                <button key={pl.id} type="button" onClick={() => togglePlatform(pl.id)} data-testid={`brand-default-${pl.id}`}
                  className={cn("rounded-full border px-2.5 py-1 text-[11.5px] transition-colors", on ? "border-[color:var(--accent-blue)] bg-[color:var(--surface-2)] text-1" : "text-muted-foreground hover:text-1")}
                  style={{ borderColor: on ? undefined : "var(--border-subtle)" }}>
                  {on && <Check className="mr-1 inline h-3 w-3 text-[color:var(--accent-cyan)]" />}{pl.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => onSave({ ...p, ...draft })} data-testid="brand-profile-save"><Save className="h-3.5 w-3.5" /> Save profile</Button>
      </div>
    </div>
  );
}

function CardEditor({ brand, onSave }) {
  const p = brand.profile || {};
  const [draft, setDraft] = useState({
    card_theme: p.card_theme || "auto", card_color: p.card_color || "",
    card_ratio: p.card_ratio || "portrait", card_format: p.card_format || "single",
    card_layout: p.card_layout || "auto",
  });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const [themes, setThemes] = useState([]);
  const [ratios, setRatios] = useState(["portrait", "square"]);
  const [layouts, setLayouts] = useState([{ id: "auto", label: "Auto — varies per post" }]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { brandApi.cardThemes().then((r) => { if (r?.themes) setThemes(r.themes); if (r?.ratios) setRatios(r.ratios); if (r?.layouts) setLayouts(r.layouts); }).catch(() => {}); }, []);

  const preview = async () => {
    setBusy(true);
    try {
      const r = await brandApi.cardPreview({ text: previewText || "Your hook goes here", theme: draft.card_theme !== "auto" ? draft.card_theme : null, brand_color: draft.card_color || null, ratio: draft.card_ratio || "portrait", card_format: draft.card_format || "single", layout: draft.card_layout !== "auto" ? draft.card_layout : null });
      if (r?.url) setPreviewUrl(mediaUrl(r.url) + `?t=${Date.now()}`);
    } catch (_) { toast.error("Couldn't render a preview"); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="brand-card">
      <div className="mb-1.5 flex items-center gap-1.5">
        <ImageIcon className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
        <h3 className="text-[13px] font-semibold text-1">Instagram card</h3>
        <span className="text-[11.5px] text-muted-foreground">— how this brand's IG image card looks by default.</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Theme</Label>
          <Select value={draft.card_theme} onValueChange={(v) => set("card_theme", v)}>
            <SelectTrigger className="mt-1.5" data-testid="brand-card-theme">
              <span className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full border border-white/20" style={{ backgroundColor: draft.card_theme === "auto" ? "transparent" : (themes.find((t) => t.id === draft.card_theme)?.accent || "transparent"), backgroundImage: draft.card_theme === "auto" ? "linear-gradient(135deg,#8b94ff,#34f4ff)" : undefined }} />
                <span className="capitalize">{draft.card_theme}</span>
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto"><span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ backgroundImage: "linear-gradient(135deg,#8b94ff,#34f4ff)" }} /> Auto</span></SelectItem>
              {themes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border border-white/20" style={{ backgroundColor: t.accent }} /> <span className="capitalize">{t.id}</span></span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Brand color <span className="lowercase tracking-normal text-muted-foreground/70">(tints any theme)</span></Label>
          <div className="mt-1.5 flex items-center gap-2">
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(draft.card_color) ? draft.card_color : "#8FE04A"} onChange={(e) => set("card_color", e.target.value)} className="h-9 w-10 shrink-0 cursor-pointer rounded border bg-transparent p-0.5" style={{ borderColor: "var(--border-subtle)" }} data-testid="brand-card-color-swatch" aria-label="brand color" />
            <Input value={draft.card_color} onChange={(e) => set("card_color", e.target.value)} placeholder="#8FE04A (optional)" className="text-[12.5px]" data-testid="brand-card-color" />
            {draft.card_color && <button type="button" onClick={() => set("card_color", "")} className="text-muted-foreground hover:text-[color:var(--accent-red)]" aria-label="clear color"><XIcon className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Ratio</Label>
          <Select value={draft.card_ratio} onValueChange={(v) => set("card_ratio", v)}>
            <SelectTrigger className="mt-1.5" data-testid="brand-card-ratio"><SelectValue /></SelectTrigger>
            <SelectContent>{ratios.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Format</Label>
          <Select value={draft.card_format} onValueChange={(v) => set("card_format", v)}>
            <SelectTrigger className="mt-1.5" data-testid="brand-card-format"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="carousel">Carousel</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Card style <span className="lowercase tracking-normal text-muted-foreground/70">(layout + background)</span></Label>
          <Select value={draft.card_layout} onValueChange={(v) => set("card_layout", v)}>
            <SelectTrigger className="mt-1.5" data-testid="brand-card-layout"><SelectValue /></SelectTrigger>
            <SelectContent>
              {layouts.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[11px] text-muted-foreground">Auto rotates a designed look per post. Pick one to lock the alignment + background for every card. Hit Preview to see it.</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Preview text</Label>
          <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="Type a hook to preview…" className="mt-1.5 text-[12.5px]" data-testid="brand-card-preview-text" />
        </div>
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={preview} disabled={busy} data-testid="brand-card-preview">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview
        </Button>
      </div>
      {previewUrl && <img src={previewUrl} alt="card preview" className="mt-3 max-h-72 rounded-lg border" style={{ borderColor: "var(--border-subtle)" }} />}
      <div className="mt-3 flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => onSave({ ...p, ...draft })} data-testid="brand-card-save"><Save className="h-3.5 w-3.5" /> Save card</Button>
      </div>
    </div>
  );
}

function VoicesEditor({ brand, onCreate, onUpdate, onDelete }) {
  const voices = brand.voices || [];
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");

  const add = async () => {
    if (!name.trim()) return toast.error("Name the voice");
    const r = await onCreate({ name: name.trim(), instructions: instructions.trim(), is_primary: voices.length === 0 });
    if (r.ok) { setName(""); setInstructions(""); toast.success("Voice added"); } else toast.error(r.error);
  };

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="brand-voices">
      <div className="mb-3 flex items-center gap-1.5">
        <Mic className="h-3.5 w-3.5 text-[color:var(--accent-cyan)]" />
        <h3 className="text-[13px] font-semibold text-1">Voices</h3>
        <span className="text-[11.5px] text-muted-foreground">— how this brand sounds. Pick one per post.</span>
      </div>

      <div className="space-y-2">
        {voices.map((v) => (
          <VoiceRow key={v.id} voice={v} onUpdate={onUpdate} onDelete={onDelete} />
        ))}
      </div>

      <div className="mt-3 rounded-lg border border-dashed p-3" style={{ borderColor: "var(--border-subtle)" }}>
        <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Add a voice</Label>
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
          A good voice describes <span className="text-1">how it sounds</span>: tone, sentence length, words to use or avoid, and personality —
          not the topic. Tap a starter to fill it in, then tweak.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="voice-presets">
          {VOICE_PRESETS.map((vp) => (
            <button key={vp.name} type="button" onClick={() => { if (!name.trim()) setName(vp.name); setInstructions(vp.instructions); }}
              className="rounded-full border px-2.5 py-1 text-[11.5px] text-muted-foreground transition-colors hover:text-1" style={{ borderColor: "var(--border-subtle)" }}
              data-testid={`voice-preset-${vp.name.toLowerCase()}`}>
              <Sparkles className="mr-1 inline h-3 w-3 text-[color:var(--accent-cyan)]" />{vp.name}
            </button>
          ))}
        </div>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Voice name (e.g. Founder, Casual)" className="mt-2.5" data-testid="voice-new-name" />
        <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} placeholder="How it sounds — e.g. 'Warm and direct. Short sentences. Plain words, no jargon. Confident, a little wry. Never salesy.'" className="mt-2 resize-none text-[12.5px]" style={{ backgroundColor: "var(--surface-2)" }} data-testid="voice-new-instructions" />
        <div className="mt-2 flex justify-end">
          <Button size="sm" onClick={add} className="gap-1.5" data-testid="voice-add"><Plus className="h-3.5 w-3.5" /> Add voice</Button>
        </div>
      </div>
    </div>
  );
}
